import { TURNSTILE_SITE_KEY } from '@/env'
import { useCallback, useRef, useState, type MutableRefObject, type ReactElement } from 'react'
import { TurnstileWidget, type TurnstileWidgetHandle } from './TurnstileWidget'

export type UseTurnstileCaptchaOptions = {
  enabled: boolean
  action: string
}

export type UseTurnstileCaptchaResult = {
  token: string | null
  tokenRef: MutableRefObject<string | null>
  reset: () => void
  renderWidget: (className?: string) => ReactElement | null
  active: boolean
}

/**
 * When `enabled` is false or no site key is configured, `active` is false,
 * `renderWidget` returns null, and `token` stays null.
 */
export const useTurnstileCaptcha = ({ enabled, action }: UseTurnstileCaptchaOptions): UseTurnstileCaptchaResult => {
  const widgetRef = useRef<TurnstileWidgetHandle | null>(null)
  const tokenRef = useRef<string | null>(null)
  const [token, setToken] = useState<string | null>(null)

  const active = enabled && !!TURNSTILE_SITE_KEY

  const onToken = useCallback((t: string) => {
    tokenRef.current = t
    setToken(t)
  }, [])

  const onExpire = useCallback(() => {
    tokenRef.current = null
    setToken(null)
  }, [])

  const onError = useCallback(() => {
    tokenRef.current = null
    setToken(null)
  }, [])

  const reset = useCallback(() => {
    tokenRef.current = null
    setToken(null)
    widgetRef.current?.reset()
  }, [])

  const renderWidget = useCallback(
    (className?: string) => {
      if (!active) return null
      return (
        <TurnstileWidget
          ref={widgetRef}
          action={action}
          onToken={onToken}
          onExpire={onExpire}
          onError={onError}
          className={className}
        />
      )
    },
    [active, action, onToken, onExpire, onError]
  )

  return { token, tokenRef, reset, renderWidget, active }
}
